import { ArrowLeftIcon, WarningCircle } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { Button, Input } from "@/components/ui";
import { useTranslation } from "@/packages/utils";

export interface ForgotPasswordFormProps {
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isLoading?: boolean;
  error?: string | null;
  onBack: () => void;
}

const stagger = {
  hidden: { opacity: 0, y: 10 },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.06,
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94] as const,
    },
  }),
};

export function ForgotPasswordForm({
  onSubmit,
  isLoading = false,
  error,
  onBack,
}: ForgotPasswordFormProps) {
  const { t } = useTranslation(["auth"]);

  return (
    <div className="flex flex-col">
      {/* Back link */}
      <motion.button
        type="button"
        onClick={onBack}
        custom={0}
        variants={stagger}
        initial="hidden"
        animate="show"
        className="flex items-center gap-1.5 text-[13px] text-secondary hover:text-primary transition-colors mb-8 self-start"
      >
        <ArrowLeftIcon className="size-3.5" />
        {t("auth:forgotPassword.backToSignIn")}
      </motion.button>

      {/* Header */}
      <motion.div custom={1} variants={stagger} initial="hidden" animate="show">
        <h1 className="text-[26px] font-bold tracking-[-0.02em] text-primary">
          {t("auth:forgotPassword.title")}
        </h1>
        <p className="mt-2 text-[14px] text-secondary leading-relaxed">
          {t("auth:forgotPassword.subtitle")}
        </p>
      </motion.div>

      {/* Form */}
      <form onSubmit={onSubmit} className="mt-8 flex flex-col gap-5">
        {/* Email */}
        <motion.div
          custom={2}
          variants={stagger}
          initial="hidden"
          animate="show"
          className="flex flex-col gap-1.5"
        >
          <label
            htmlFor="email"
            className="text-[13px] font-medium text-secondary"
          >
            {t("auth:forgotPassword.emailLabel")}
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder={t("auth:forgotPassword.emailPlaceholder")}
            required
            autoComplete="email"
            autoFocus
          />
        </motion.div>

        {/* Error */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            className="flex items-start gap-2 rounded-lg bg-red-50 px-3 py-2.5"
          >
            <WarningCircle
              className="mt-0.5 size-4 shrink-0 text-red-500"
              weight="fill"
            />
            <p className="text-[13px] text-red-600">{error}</p>
          </motion.div>
        )}

        {/* Submit */}
        <motion.div
          custom={3}
          variants={stagger}
          initial="hidden"
          animate="show"
        >
          <Button
            type="submit"
            variant="primary"
            loading={isLoading}
            className="mt-1 w-full"
          >
            {t("auth:forgotPassword.submit")}
          </Button>
        </motion.div>
      </form>
    </div>
  );
}
